import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Calendar,
  Clock,
  Star,
  ArrowUp,
  ArrowDown,
  Scissors
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const RevenueDashboard = ({ token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [clientAnalytics, setClientAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("revenue");

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [revenueRes, clientRes] = await Promise.all([
        axios.get(`${API}/analytics/revenue`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAnalytics(revenueRes.data);
      setClientAnalytics(clientRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: "revenue", label: "Revenue", icon: DollarSign },
          { id: "clients", label: "Clients", icon: Users },
          { id: "services", label: "Services", icon: Scissors }
        ].map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? "default" : "outline"}
            className={`flex items-center gap-2 whitespace-nowrap ${
              activeTab === tab.id 
                ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white" 
                : "border-orange-200"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Revenue Tab */}
      {activeTab === "revenue" && analytics && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs font-medium">This Month</span>
                </div>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(analytics.monthly_revenue)}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs font-medium">Appointments</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">
                  {analytics.total_appointments}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-medium">Avg Ticket</span>
                </div>
                <p className="text-2xl font-bold text-purple-700">
                  {formatCurrency(analytics.average_ticket)}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-orange-600 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs font-medium">Total Revenue</span>
                </div>
                <p className="text-2xl font-bold text-orange-700">
                  {formatCurrency(analytics.total_revenue)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Busiest Hours */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                Busiest Hours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics.busiest_hours?.map(([hour, count], index) => (
                  <div key={hour} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                      index === 0 ? "bg-orange-500 text-white" : 
                      index === 1 ? "bg-orange-200 text-orange-700" : 
                      "bg-gray-100 text-gray-600"
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{hour}:00</p>
                      <p className="text-sm text-gray-500">{count} bookings</p>
                    </div>
                    <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                        style={{ width: `${(count / (analytics.busiest_hours[0]?.[1] || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
                {(!analytics.busiest_hours || analytics.busiest_hours.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No booking data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Booth Revenue (for owners) */}
          {analytics.booth_revenue > 0 && (
            <Card className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Booth Rent Collected</p>
                    <p className="text-3xl font-bold">{formatCurrency(analytics.booth_revenue)}</p>
                  </div>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Clients Tab */}
      {activeTab === "clients" && clientAnalytics && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{clientAnalytics.total_clients}</p>
                <p className="text-sm text-gray-500">Total Clients</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 text-center">
                <Star className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <p className="text-2xl font-bold">{clientAnalytics.repeat_clients}</p>
                <p className="text-sm text-gray-500">Repeat Clients</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-gradient-to-br from-green-500 to-emerald-500 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Client Retention</p>
                  <p className="text-3xl font-bold">{clientAnalytics.retention_rate?.toFixed(1)}%</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Clients */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Top Clients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {clientAnalytics.top_clients?.map((client, index) => (
                  <div key={client.client_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? "bg-amber-500 text-white" : "bg-gray-200 text-gray-600"
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{client.name}</p>
                      <p className="text-sm text-gray-500">{client.visits} visits</p>
                    </div>
                  </div>
                ))}
                {(!clientAnalytics.top_clients || clientAnalytics.top_clients.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No client data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Services Tab */}
      {activeTab === "services" && analytics && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Scissors className="w-5 h-5 text-orange-500" />
              Top Services by Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.top_services?.map((service, index) => (
                <div key={service.service} className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${
                    index === 0 ? "bg-gradient-to-br from-orange-500 to-amber-500 text-white" : 
                    index === 1 ? "bg-orange-100 text-orange-600" : 
                    "bg-gray-100 text-gray-600"
                  }`}>
                    #{index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{service.service}</p>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mt-1">
                      <div 
                        className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                        style={{ width: `${(service.revenue / (analytics.top_services[0]?.revenue || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                  <p className="font-bold text-gray-700">{formatCurrency(service.revenue)}</p>
                </div>
              ))}
              {(!analytics.top_services || analytics.top_services.length === 0) && (
                <p className="text-gray-500 text-center py-4">No service data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RevenueDashboard;
